import { useState, useEffect, useCallback } from 'react';
import type { Stage, Opportunity, ContactSummary } from '../types';
import * as api from '../api';

interface UsePipelineDataResult {
  stages: Stage[];
  opportunities: Opportunity[];
  contacts: Map<string, ContactSummary>;
  loading: boolean;
  error: string | null;
  refreshData: () => Promise<void>;
  moveOpportunity: (
    oppId: string,
    newStageId: string,
    newProbability?: number
  ) => Promise<boolean>;
}

export function usePipelineData(): UsePipelineDataResult {
  const [stages, setStages] = useState<Stage[]>([]);
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [contacts, setContacts] = useState<Map<string, ContactSummary>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [stagesData, oppsData] = await Promise.all([
        api.fetchStages(),
        api.fetchOpportunities(),
      ]);

      // Sort stages by order
      const sortedStages = [...stagesData].sort((a, b) => a.order - b.order);
      setStages(sortedStages);
      setOpportunities(oppsData);

      // Fetch contacts for all opportunities that have a contact_id
      const contactIds = [...new Set(
        oppsData
          .map(o => o.contact_id)
          .filter((id): id is string => !!id)
      )];

      if (contactIds.length > 0) {
        try {
          const contactsData = await api.fetchContacts(contactIds);
          const contactMap = new Map(contactsData.map(c => [c.id, c]));
          setContacts(contactMap);
        } catch {
          // Contacts are optional, don't fail the whole load
          console.warn('Failed to load contacts');
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load pipeline data');
    } finally {
      setLoading(false);
    }
  }, []);

  // Move opportunity with optimistic update
  const moveOpportunity = useCallback(async (
    oppId: string,
    newStageId: string,
    newProbability?: number
  ): Promise<boolean> => {
    const opp = opportunities.find(o => o.id === oppId);
    if (!opp) return false;

    const previousStageId = opp.stage_id;
    const previousProbability = opp.probability;

    // Optimistic update
    setOpportunities(prev =>
      prev.map(o =>
        o.id === oppId
          ? { ...o, stage_id: newStageId, probability: newProbability ?? o.probability }
          : o
      )
    );

    try {
      await api.transitionOpportunityStage(oppId, newStageId, newProbability);
      return true;
    } catch (err) {
      // Rollback on error
      setOpportunities(prev =>
        prev.map(o =>
          o.id === oppId
            ? { ...o, stage_id: previousStageId, probability: previousProbability }
            : o
        )
      );
      setError(err instanceof Error ? err.message : 'Failed to move deal');
      return false;
    }
  }, [opportunities]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  return {
    stages,
    opportunities,
    contacts,
    loading,
    error,
    refreshData: loadData,
    moveOpportunity,
  };
}

// Calculate days in stage from stage history
export function getDaysInStage(opp: Opportunity): number {
  if (!opp.stage_history || opp.stage_history.length === 0) {
    // Fall back to created_at if no history
    const created = new Date(opp.created_at);
    const now = new Date();
    return Math.floor((now.getTime() - created.getTime()) / (1000 * 60 * 60 * 24));
  }

  const currentStage = opp.stage_history[opp.stage_history.length - 1];
  const entered = new Date(currentStage.entered_at);
  const now = new Date();
  return Math.floor((now.getTime() - entered.getTime()) / (1000 * 60 * 60 * 24));
}

// Format currency for display
export function formatCurrency(amount: number, currency: string = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

// Calculate total value of opportunities in a stage
export function calculateStageTotal(
  opportunities: Opportunity[],
  stageId: string
): number {
  return opportunities
    .filter(o => o.stage_id === stageId)
    .reduce((sum, o) => sum + o.amount, 0);
}
